import React, { useRef, useState } from 'react';
import { OV_STRINGS } from '../../constants/strings';
import { today } from '../../utils/date';
import styles from './FileUploadSection.module.scss';

const MAX_FILES = 10;
const MAX_FILE_SIZE = 100 * 1024 * 1024;
const MAX_TOTAL_SIZE = 100 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'heic', 'pdf', 'hwp', 'doc', 'docx', 'xls', 'xlsx', 'zip'];
const ACCEPT_STRING = ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(',');

function getFileExtension(filename) {
  return (filename.split('.').pop() || '').toLowerCase();
}

function getTotalSize(fileList) {
  return fileList.reduce((sum, f) => sum + f.size, 0);
}

function FileUploadSection({ title, files, onFilesChange, children }) {
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState('');

  const validateAndAdd = (incoming) => {
    setFileError('');
    const newFiles = Array.from(incoming);

    const invalidType = newFiles.find(f => !ALLOWED_EXTENSIONS.includes(getFileExtension(f.name)));
    if (invalidType) {
      setFileError(OV_STRINGS.ERROR_FILE_TYPE);
      return;
    }

    const oversized = newFiles.find(f => f.size > MAX_FILE_SIZE);
    if (oversized) {
      setFileError(OV_STRINGS.ERROR_FILE_SIZE);
      return;
    }

    if (files.length + newFiles.length > MAX_FILES) {
      setFileError(OV_STRINGS.ERROR_FILE_COUNT);
      return;
    }

    const totalSize = getTotalSize(files) + getTotalSize(newFiles);
    if (totalSize > MAX_TOTAL_SIZE) {
      setFileError(OV_STRINGS.ERROR_FILE_TOTAL_SIZE);
      return;
    }

    onFilesChange([...files, ...newFiles]);
  };

  const handleFileAdd = (e) => {
    validateAndAdd(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemove = (index) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className={styles.section}>
      {title && <strong className={styles.title}>{title}</strong>}

      <div
        className={styles.uploadArea}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          validateAndAdd(e.dataTransfer.files);
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_STRING}
          onChange={handleFileAdd}
          style={{ display: 'none' }}
        />
        <span className={styles.uploadText}>{OV_STRINGS.RECIPIENT_ATTACHMENT_UPLOAD}</span>
        <span className={styles.uploadLimit}>{OV_STRINGS.RECIPIENT_ATTACHMENT_LIMIT}</span>
      </div>

      {fileError && <p className={styles.fileError}>{fileError}</p>}

      {files.length > 0 && (
        <div className={styles.fileList}>
          {files.map((f, i) => (
            <div key={i} className={styles.fileItem}>
              <span className={styles.fileName}>{f.name}</span>
              <div className={styles.fileRight}>
                <span className={styles.fileDate}>{today()}</span>
                <button
                  type="button"
                  className={styles.fileDelete}
                  onClick={() => handleRemove(i)}
                  aria-label="삭제"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="#989ba2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {children}
    </div>
  );
}

export default FileUploadSection;
